/**
 * Thin fetch wrapper that:
 *   1. Prepends VITE_API_BASE (empty in dev → routed through Vite proxy).
 *   2. Injects the Bearer access token if `getAccessToken` is provided.
 *   3. Throws an `ApiError` on non-2xx, preserving the status code for TanStack
 *      Query's retry policy in queryClient.ts.
 *
 * Usage:
 *   const me = await apiClient.get<Me>('/api/me', { getAccessToken });
 *
 * We deliberately keep this small and dependency-free. When we wire up
 * NSwag/Kiota codegen for the typed client, this stays as the underlying
 * transport — the codegen sits on top.
 */

const base = import.meta.env.VITE_API_BASE ?? '';

export class ApiError extends Error {
  // Declared explicitly rather than via constructor parameter properties because
  // `erasableSyntaxOnly` in tsconfig forbids the `public readonly` shorthand —
  // it would emit runtime assignments and isn't pure type-erasure.
  public readonly status: number;
  public readonly body?: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface ApiOptions {
  getAccessToken?: () => Promise<string>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

async function request<T>(method: string, path: string, body: unknown, opts: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...opts.headers
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.getAccessToken) {
    headers.Authorization = `Bearer ${await opts.getAccessToken()}`;
  }

  const response = await fetch(`${base}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: opts.signal
  });

  const text = await response.text();
  const parsed = text ? safeJson(text) : undefined;

  if (!response.ok) {
    throw new ApiError(response.status, `${method} ${path} → ${response.status}`, parsed ?? text);
  }
  return parsed as T;
}

function safeJson(s: string): unknown {
  try { return JSON.parse(s); } catch { return s; }
}

export const apiClient = {
  get:  <T>(p: string, o?: ApiOptions)                       => request<T>('GET',    p, undefined, o),
  post: <T>(p: string, b: unknown, o?: ApiOptions)           => request<T>('POST',   p, b, o),
  put:  <T>(p: string, b: unknown, o?: ApiOptions)           => request<T>('PUT',    p, b, o),
  del:  <T>(p: string, o?: ApiOptions)                       => request<T>('DELETE', p, undefined, o)
};
